import React, { useState } from 'react';
import './SectionPliable.css';

interface SectionPliableProps {
  // Titre du bandeau (texte ou JSX, ex. avec un asterisque).
  title: React.ReactNode;
  children: React.ReactNode;
  // Ouverte par defaut.
  defaultOpen?: boolean;
  // Contenu additionnel a droite du bandeau (ex. statut de sauvegarde) ; ne plie pas la section.
  headerRight?: React.ReactNode;
  className?: string;
  // Verrou : bandeau grise + cadenas, section non depliable. Comportement et cadenas repris de
  // `OperationAnalyseAccordion` (MS) plutot que reinventes — c'est le motif deja en place dans
  // l'application (sections de l'onglet Analyse, prolongation de delai). Porte ICI et non copie une
  // 9e fois : SectionPliable est le composant que les vues BC utilisent deja.
  locked?: boolean;
  // Precision affichee a cote du titre quand la section est verrouillee (ce qui la deverrouillera).
  lockedHint?: React.ReactNode;
  // Laisse le corps visible malgre le verrou (cas « lecture seule » du MS). Par defaut le corps est
  // masque : une section verrouillee n'a rien a montrer.
  lockedKeepOpen?: boolean;
  // Leve le clip de la carte. A n'activer que pour une section dont un champ ouvre une liste flottante
  // (autocompletion d'adresse) : le clip qui arrondit le bas de la carte coupe aussi cette liste des
  // que le champ est le dernier de la section.
  overflowVisible?: boolean;
}

/**
 * Section a bandeau gris pliable/depliable (clic sur le bandeau), ouverte par defaut.
 * Memes codes que les sections des marches subsequents.
 */
const SectionPliable: React.FC<SectionPliableProps> = ({
  title,
  children,
  defaultOpen = true,
  headerRight,
  className,
  locked = false,
  lockedHint,
  lockedKeepOpen = false,
  overflowVisible = false,
}) => {
  const [open, setOpen] = useState<boolean>(defaultOpen);
  // Verrouillee : le corps ne s'ouvre pas, sauf `lockedKeepOpen`. L'etat interne est conserve tel
  // quel pour que la section retrouve son pli d'origine des que le verrou tombe.
  const estOuverte = locked ? lockedKeepOpen : open;
  const toggle = () => {
    if (locked) return;
    setOpen((o) => !o);
  };

  return (
    <div
      className={`section-pliable${locked ? ' section-pliable--locked' : ''}${
        overflowVisible ? ' section-pliable--overflow-visible' : ''
      }${className ? ` ${className}` : ''}`}
    >
      <div
        className="section-pliable-header"
        onClick={toggle}
        role="button"
        tabIndex={locked ? -1 : 0}
        aria-expanded={estOuverte}
        aria-disabled={locked}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <span className="section-pliable-title">
          {locked && (
            <svg className="section-pliable-lock" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
              <path
                fill="currentColor"
                d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5Zm3 8H9V6a3 3 0 0 1 6 0v3Z"
              />
            </svg>
          )}
          <span>{title}</span>
          {locked && lockedHint && <span className="section-pliable-hint">{lockedHint}</span>}
        </span>
        <span className="section-pliable-right">
          {headerRight && (
            <span className="section-pliable-extra" onClick={(e) => e.stopPropagation()}>{headerRight}</span>
          )}
          <svg
            className={`section-pliable-chevron${estOuverte ? '' : ' is-closed'}`}
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>
      {estOuverte && <div className="section-pliable-content">{children}</div>}
    </div>
  );
};

export default SectionPliable;
